// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { createHandlerChainRunner, Handler } from "@insite/client-framework/HandlerCreator";
import { AnyAction } from "@insite/client-framework/Store/Reducers";
import { getProductCollectionV1 } from "@stoneage/App/Services/ProductServiceV1/ProductServiceV1";
import {
    BillOfMaterial,
    QuickProductLookupBaseModel,
} from "@stoneage/App/Services/QuickProductLookup/QuickProductLookupService";
import { CustomAppState } from "@stoneage/App/Store/Reducers";
import { setUrlsToQuickLookupProducts } from "@stoneage/App/Widgets/QuickProductLookup/QuickProductLookupHelpers";

type Parameter = {
    filter: string;
};

type HandlerType = Handler<Parameter, {}>;

export const FilterLookupProducts: HandlerType = async props => {
    const { filter } = props.parameter;
    const customAppState = props.getState() as CustomAppState;
    const products = customAppState.xProductLookup.products?.dealerPrices;
    let pagination = customAppState.xProductLookup.pagination;
    if (products) {
        let finalFilterdProducts = [];

        if (filter?.length > 0) {
            const filterString = filter
                .replace(/ /g, "")
                .replace(/[^a-zA-Z0-9 ]/g, "")
                .toLowerCase();

            const filteredProductsFirstLevelExactMatch: QuickProductLookupBaseModel[] = [];
            const filteredProductsSecondLevelExactMatch: QuickProductLookupBaseModel[] = [];
            const filteredProductsByPartNumberAndDescription: QuickProductLookupBaseModel[] = [];
            products.forEach(product => {
                let foundInChild = false;
                let foundExactMatchInChild = false;
                let foundExactMatchInParent = false;
                if (filter === product.productNumber || filter.toLowerCase() === product.productNumber.toLowerCase()) {
                    foundExactMatchInParent = true;
                    filteredProductsFirstLevelExactMatch.push(product);
                } else if (product.billOfMaterials) {
                    product.billOfMaterials.forEach(child => {
                        if (filter === child.partNumber || filter.toLowerCase() === child.partNumber.toLowerCase()) {
                            foundExactMatchInChild = true;
                        } else if (
                            child.partNumber
                                .toLowerCase()
                                .replace(/ /g, "")
                                .replace(/[^a-zA-Z0-9 ]/g, "")
                                .includes(filterString) ||
                            child.description
                                .toLowerCase()
                                .replace(/ /g, "")
                                .replace(/[^a-zA-Z0-9 ]/g, "")
                                .includes(filterString)
                        ) {
                            foundInChild = true;
                        }
                    });
                    if (foundExactMatchInChild) {
                        filteredProductsSecondLevelExactMatch.push(product);
                    } else if (foundInChild) {
                        filteredProductsByPartNumberAndDescription.push(product);
                    }
                }

                if (!foundInChild && !foundExactMatchInChild && !foundExactMatchInParent) {
                    if (
                        product.productNumber
                            .toLowerCase()
                            .replace(/ /g, "")
                            .replace(/[^a-zA-Z0-9 ]/g, "")
                            .includes(filterString) ||
                        product.productDescription
                            .toLowerCase()
                            .replace(/ /g, "")
                            .replace(/[^a-zA-Z0-9 ]/g, "")
                            .includes(filterString)
                    ) {
                        filteredProductsByPartNumberAndDescription.push(product);
                    }
                }
            });
            finalFilterdProducts = [
                ...filteredProductsFirstLevelExactMatch,
                ...filteredProductsSecondLevelExactMatch,
                ...filteredProductsByPartNumberAndDescription,
            ];
        } else {
            finalFilterdProducts = [...products];
        }

        pagination = {
            ...pagination,
            currentPage: 1,
            page: 1,
            totalItemCount: finalFilterdProducts.length,
            numberOfPages: Math.ceil(finalFilterdProducts.length / pagination.pageSize),
        };
        let paginatedProducts = finalFilterdProducts.slice(0, pagination.pageSize);

        const productPartNumbers = paginatedProducts.map(product => product.productNumber);

        if (productPartNumbers.length !== 0) {
            try {
                const { products } = await getProductCollectionV1({ erpNumbers: productPartNumbers });
                paginatedProducts = setUrlsToQuickLookupProducts(products, paginatedProducts);
            } catch (error: any) {
                /* empty */
            }
        } else {
            const startIndex = (pagination.currentPage - 1) * pagination.pageSize;
            const endIndex = (pagination.currentPage - 1) * pagination.pageSize + pagination.pageSize;
            const partNumbers = finalFilterdProducts.slice(startIndex, endIndex).map(product => {
                return product.productNumber;
            });

            try {
                const { products } = await getProductCollectionV1({ erpNumbers: partNumbers });
                paginatedProducts = setUrlsToQuickLookupProducts(products, paginatedProducts);
            } catch (error: any) {
                /* empty */
            }
        }
        props.dispatch({
            type: "Pages/ProductLookup/SetFilteredProducts",
            filteredProducts: finalFilterdProducts,
            paginatedProducts,
            pagination,
        } as unknown as AnyAction);
    }
};

export const chain = [FilterLookupProducts];

const filterLookupProducts = createHandlerChainRunner(chain, "FilterLookupProducts");
export default filterLookupProducts;
